import {
	commentAutopilotAccessGuard,
	commentAutopilotEnabledGuard
} from './modules/comment-autopilot/guards/comment-autopilot-enabled.guard';
import { inject, NgModule } from '@angular/core';
import { Router, RouterModule, Routes } from '@angular/router';
import {
	affiliatesAccessGuard,
	socialListeningAccessGuard
} from '@brand-lolly/app/shared/guards/subscription-required.guard';

// Old child segment → new one, for the `/projects` redirects below. Identity today:
// the tabs are restructured in a later slice of this change (Overview / roster /
// Messages), and this table is what that slice edits — one place, rather than a
// redirect per tab scattered through the route list.
const CHILD_TAB_REDIRECTS: ReadonlyArray<readonly [string, string]> = [
	['info', 'info'],
	// Identity, and it has to stay that way: a bookmarked link to the search's
	// influencer list is a link to THAT table, not to the campaign roster that
	// replaced it in the tab strip for a while. The roster has its own path.
	['influencers', 'influencers'],
	['messaging-settings', 'messaging-settings']
];

const routes: Routes = [
	{
		// Home is the app landing.
		path: '',
		loadComponent: () =>
			import(
				'@brand-lolly/app/social-media-influencer/modules/home-page/home-page.component'
			).then(m => m.HomePageComponent),
		// `shellBackground` is the page declaring what the shell paints behind
		// the Ask Ana panel, so the panel's rounded corner meets this page's
		// grey instead of an unrelated white. Home is the only page with a
		// background of its own today; a route that declares nothing gets the
		// shell's default.
		// CONTRACT: anaComposer false hides the shell's Ana input — Home has its own.
		data: { preload: true, shellBackground: '#fafafa', anaComposer: false }
	},
	{
		// AI Search now lives at /ai-search. Open: the search is unlocked on
		// every application tier including the free one, and what bounds it is
		// the credit price of a search rather than a plan. Starting a chat used
		// to raise a Discovery paywall inside the component; that went with the
		// product.
		path: 'ai-search',
		loadChildren: () =>
			import(
				'@brand-lolly/app/social-media-influencer/modules/ai-agent-page/ai-agent-page.module'
			).then(m => m.AiAgentPageModule),
		data: { preload: true }
	},
	{
		// Campaigns. The module directory was named `ai-projects-page` until the
		// rename landed on its own, so a search of the history for either name
		// finds this route. Its files still carry `project-*` names where they
		// genuinely describe a search run (`project-influencers`, `project-info`)
		// rather than the campaign.
		//
		// **No Discovery guard, and its removal moved in the same commit that
		// deleted `BULK_MESSAGING_ENABLED`.** The guard was inherited from
		// `/projects`, and it was right while campaigns WERE search projects. It is
		// not any more: department outreach creates campaigns for brands that never
		// bought Discovery, so the guard would open a Discovery paywall to a Social
		// Listening brand trying to see its own outreach. The backend made the same
		// correction — the gate sits on the search import, the one operation
		// Discovery actually sells, and not on the aggregate.
		path: 'campaigns',
		loadChildren: () =>
			import(
				'@brand-lolly/app/social-media-influencer/modules/campaigns/campaigns.module'
			).then(m => m.CampaignsModule),
		data: { preload: true }
	},
	{
		// A destination, not a campaign tab. The question it answers names no
		// campaign — it is asked by somebody who no longer remembers which one it
		// was — and it is ungated for the reason `campaigns` above is: gating a
		// library on one department shows a brand that bought another an empty
		// page rather than a closed one.
		path: 'content',
		loadChildren: () =>
			import(
				'@brand-lolly/app/social-media-influencer/modules/content-library/content-library.routes'
			).then(m => m.CONTENT_LIBRARY_ROUTES)
	},
	{
		// Everything the brand owes creators, across every department, in one
		// ledger. Ungated for the reason `content` above is: a brand pays
		// campaign fees and affiliate commission alike, and the backend serves
		// `/payments/*` without a subscription gate.
		path: 'payments',
		loadChildren: () =>
			import(
				'@brand-lolly/app/social-media-influencer/modules/payments/payments.routes'
			).then(m => m.PAYMENTS_ROUTES)
	},
	// The old path, kept working. This is the least visible part of the rename and
	// the one with the longest tail: a brand clicking a week-old notification email,
	// a bookmarked campaign, a link pasted into a shared doc. All of them land here.
	//
	// The child segment is carried across rather than dropped, because a link to a
	// specific tab that lands on the campaign's first tab is a worse answer than the
	// tab that was asked for. `CHILD_TAB_REDIRECTS` is the one place the mapping
	// lives; when the tabs are restructured it is the only thing that changes.
	{ path: 'projects', redirectTo: 'campaigns', pathMatch: 'full' },
	...CHILD_TAB_REDIRECTS.map(([from, to]) => ({
		path: `projects/:id/${from}`,
		redirectTo: `campaigns/:id/${to}`
	})),
	// An unrecognised child — a tab that has since been removed, or a typo in a
	// pasted URL — resolves to the campaign rather than to a 404.
	//
	// ONE prefix-matching route with a FUNCTION redirect, and both halves of that
	// are load bearing.
	//
	// It used to be two lines, the second being `projects/:id/**`. Angular does
	// not support a partial wildcard — `**` is only a path of its own — so that
	// route never matched anything, `/projects/<id>/a-tab-that-never-existed`
	// fell through to the app's global `**`, and the brand got /404. Precisely
	// what those lines existed to prevent.
	//
	// A string `redirectTo` cannot fix it either: Angular APPENDS the unmatched
	// tail to a string target, so a prefix match would produce
	// `/campaigns/<id>/a-tab-that-never-existed` — no child matches that, and it
	// is /404 again by a longer route. Returning a `UrlTree` drops the tail,
	// which is the whole point: the tab is gone, the campaign is not.
	{
		path: 'projects/:id',
		redirectTo: ({ params }) =>
			inject(Router).parseUrl(`/campaigns/${params['id']}`)
	},
	{
		// Partner Ads. UNGATED since 2026-09-15, and the upsell page that used to
		// sit at `partner-ads/upgrade` went with the gate: the owner opened the
		// department on every tier, `feature.partner_ads` is seeded `true`
		// everywhere, and the backend dropped `require_tier_feature` from the
		// whole `meta_ads` tree — so nothing could redirect there and the page
		// would only have offered an upgrade for something the brand already
		// has. What bounds a free account here is `max_ad_accounts`, at the
		// action, not at the door.
		path: 'partner-ads',
		loadChildren: () =>
			import(
				'@brand-lolly/app/social-media-influencer/modules/partner-ads/partner-ads.module'
			).then(m => m.PartnerAdsModule),
		data: { preload: true }
	},
	{
		// The inbox. UNGATED, and its gate is the one the owner hit: it sat
		// behind `ProductAlias.DISCOVERY` and answered a unified subscriber with
		// a "Discovery plan required" dialog and a bounce to the plans page, for
		// a product that no longer exists to be bought. Nothing on the backend
		// gates messaging either — `/messaging/programs`, `/direct-messages`,
		// `/templates` and `/follow-ups` all mount without a billing dependency
		// — so the client was the only thing closing it.
		path: 'messaging',
		loadChildren: () =>
			import(
				'@brand-lolly/app/social-media-influencer/modules/ai-messaging-page/ai-messaging-page.module'
			).then(m => m.AiMessagingPageModule),
		data: { preload: true }
	},
	{
		// Full-screen upsell shown when the brand lacks the Affiliate
		// subscription. MUST precede the lazy 'affiliates' route so this exact
		// path matches first; it carries no subscription guard, so the access
		// guard below can redirect here without looping.
		path: 'affiliates/upgrade',
		loadComponent: () =>
			import(
				'./modules/affiliates/pages/upgrade/affiliates-upgrade.page'
			).then(m => m.AffiliatesUpgradePage),
		data: { preload: true }
	},
	{
		path: 'comment-autopilot',
		loadChildren: () =>
			import('./modules/comment-autopilot/comment-autopilot.module').then(
				m => m.CommentAutopilotModule
			),
		// Behind a build flag. `canMatch` rather than `canActivate`: on a build
		// that does not carry the flag the route does not exist at all, so the
		// address falls through to the app's `**` and answers 404 instead of
		// matching and then refusing.
		// Then the per-brand switch: a brand a platform admin has not enabled gets
		// the same not-found answer. The order matters — the router stops at the
		// first guard that refuses, so a build without the flag never asks.
		canMatch: [commentAutopilotEnabledGuard, commentAutopilotAccessGuard]
	},
	{
		path: 'affiliates',
		loadChildren: () =>
			import('./modules/affiliates/affiliates.module').then(
				m => m.AffiliatesModule
			),
		// No Affiliate subscription → redirect to the in-shell upsell screen
		// above, instead of the paywall dialog (which other products still use).
		canActivate: [affiliatesAccessGuard],
		data: { preload: true }
	},
	{
		// Creator search and saved lists. Ungated for the same reason as
		// `/messaging`: `feature.discovery` is on every tier, and the backend's
		// `/api/v1/discovery` and `/api/v1/discovery-searches` trees carry no
		// billing dependency. A profile costs credits when it is opened, which
		// is where the bound belongs.
		path: 'discovery',
		loadChildren: () =>
			import('./modules/discovery-v2/discovery-v2.module').then(
				m => m.DiscoveryV2Module
			),
		data: { preload: true }
	},
	{
		path: 'list',
		loadChildren: () =>
			import('./modules/discovery-v2/discovery-v2.module').then(
				m => m.DiscoveryV2Module
			),
		data: { preload: true }
	},
	{
		// Full-screen upsell shown when the brand's tier is below Business or
		// its plan is past due. MUST precede the lazy 'social-listening' route so this
		// exact path matches first; it carries no subscription guard, so the
		// access guard below can redirect here without looping.
		path: 'social-listening/upgrade',
		loadComponent: () =>
			import(
				'./modules/social-listening/pages/upsell/social-listening-upsell.component'
			).then(m => m.SocialListeningUpsellComponent),
		data: { preload: true }
	},
	{
		path: 'social-listening',
		loadChildren: () =>
			import('./modules/social-listening/social-listening.module').then(
				m => m.SocialListeningModule
			),
		// No Social Listening access (below Business, or past due) → redirect
		// to the in-shell upsell screen above, instead of the paywall dialog.
		canActivate: [socialListeningAccessGuard],
		data: { preload: true }
	},
	{
		path: 'chrome-extension',
		loadChildren: () =>
			import('./modules/chrome-extension/chrome-extension.module').then(
				m => m.ChromeExtensionModule
			),
		data: { preload: true }
	}
];

@NgModule({
	imports: [RouterModule.forChild(routes)],
	exports: [RouterModule]
})
export class SocialMediaInfluencerRoutingModule {}
